import {
  Box,
  Flex,
  IconButton,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import { useGetVideoVoicesQuery } from "store/aiHub/aiGenerationApi";
import { truncateName } from "utils/functions";

const CustomAudioPlayer = ({
  id,
  src,
  currentAudio,
  setCurrentAudio,
  currentAudioId,
  setCurrentAudioId,
}) => {
  const audioRef = useRef(null);
  const isPlaying = currentAudioId === id;

  const togglePlay = () => {
    if (!audioRef.current) return;

    // If this is already playing, pause it
    if (isPlaying) {
      audioRef.current.pause();
      setCurrentAudio(null);
      setCurrentAudioId(null);
    } else {
      // Pause the current one
      if (currentAudio && currentAudio !== audioRef.current) {
        currentAudio.pause();
      }

      audioRef.current.play();
      setCurrentAudio(audioRef.current);
      setCurrentAudioId(id);
    }
  };

  useEffect(() => {
    const ref = audioRef.current;
    if (!ref) return;

    const handleEnded = () => {
      setCurrentAudio(null);
      setCurrentAudioId(null);
    };

    ref.addEventListener("ended", handleEnded);
    return () => ref.removeEventListener("ended", handleEnded);
  }, [setCurrentAudio, setCurrentAudioId]);

  return (
    <>
      <audio ref={audioRef} src={src} hidden />
      <IconButton
        icon={isPlaying ? <FaPause /> : <FaPlay />}
        onClick={togglePlay}
        aria-label="Play/Pause"
        size="sm"
      />
    </>
  );
};

export default function VoicesTabPanel({
  selectedTab,
  onSelectVoice,
  selectedVoice,
  selectedVoiceAccent,
  onSelectAccent
}) {

  const hoverColor = useColorModeValue('brand.100', 'purple.800');
  const checkColor = useColorModeValue('brand.800', 'gray.50');
  const checkBg = useColorModeValue('brand.200', 'purple.500');

  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentAudioId, setCurrentAudioId] = useState(null);

  const { data: videoVoices, isLoading: isVideoVoicesLoading } = useGetVideoVoicesQuery(undefined, {
    skip: selectedTab !== 'voices',
  });

  return (
    <Box overflowY="auto" height="70vh" w={'full'}>
      {isVideoVoicesLoading ?
        <Flex alignItems="center" justifyContent="center" h="50vh">
          <Spinner size="md" />
        </Flex>
        :
        <SimpleGrid columns={[1, 2]} spacing={4} px={3}>
          {videoVoices?.flatMap((voice, voiceIndex) =>
            voice.accents.map((accent, accentIndex) => {
              const key = `${voice.name}-${accent.accent_name}-${voiceIndex}-${accentIndex}`;

              return (
                <Box
                  key={key}
                  p={2}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={
                    selectedVoice?.name === voice?.name &&
                      selectedVoiceAccent?.id === accent.id
                      ? checkColor
                      : "brand.200"
                  }
                  bg={
                    selectedVoice?.name === voice?.name &&
                    selectedVoiceAccent?.id === accent.id
                      ? checkBg
                      : "transparent"
                  }
                  _hover={{ bg: hoverColor, cursor: "pointer" }}
                  onClick={() => {
                    onSelectVoice(voice);
                    onSelectAccent(accent);
                  }}
                  transition="background 0.2s"
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  height="100%"
                  gap={4}
                >
                  {/* Audio Controls */}
                  <Box>
                    {accent.preview_url && (
                      <CustomAudioPlayer
                        id={key}
                        src={accent.preview_url}
                        currentAudio={currentAudio}
                        setCurrentAudio={setCurrentAudio}
                        currentAudioId={currentAudioId}
                        setCurrentAudioId={setCurrentAudioId}
                      />
                    )}
                  </Box>

                  {/* Voice Info */}
                  <Flex direction="column" gap={1}>
                    <Text fontWeight="bold" fontSize="14px">
                      {truncateName(voice.name, 17)}
                    </Text>
                    <Text fontSize="12px" mb={1}>
                      {voice.gender
                        ? voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1)
                        : "Gender"}
                      {accent.accent_name ? `, ${truncateName(accent.accent_name)}` : ""}
                    </Text>
                  </Flex>
                </Box>
              );
            })
          )}
        </SimpleGrid>
      }
    </Box>
  )
}